/**
 * TranscriptSOAPEditor Component
 * 
 * Allows providers to:
 * - View the call transcript
 * - Generate AI-powered SOAP notes from transcript
 * - Edit and refine the generated notes
 * - Save the final SOAP note to patient record
 * 
 * Mobile-optimized with best UX practices for clinical workflows.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import {
  Sparkles,
  FileText,
  Save,
  Loader2,
  CheckCircle,
  AlertCircle,
  Edit3,
  RefreshCw,
  ClipboardCheck,
  MessageSquare,
  Activity,
  Brain,
  Target,
  User,
  Stethoscope,
  Clock,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { TranscriptLine } from './VideoRoom';
import { transcriptService, GeneratedSOAP } from '@/services/transcriptService';
import { emrService, EmrClinicalNote } from '@/services/emrService';

interface TranscriptSOAPEditorProps {
  transcriptLines: TranscriptLine[];
  transcriptId?: string; // Optional: if transcript is already saved
  patientId: string;
  patientName?: string;
  appointmentId?: string;
  encounterId?: string;
  clinicId: string;
  onSave?: (note: EmrClinicalNote) => void;
  onClose?: () => void;
  isOpen?: boolean;
}

// SOAP Section Component with expand/collapse
const SOAPSection: React.FC<{
  title: string;
  icon: React.ElementType;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  color: string;
  defaultExpanded?: boolean;
}> = ({ title, icon: Icon, value, onChange, placeholder, color, defaultExpanded = true }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  return (
    <div className={`border rounded-lg ${color} transition-all duration-200`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 text-left"
      >
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${color.replace('border-', 'bg-').replace('/30', '/20')}`}>
            <Icon className="h-4 w-4" />
          </div>
          <span className="font-medium text-sm">{title}</span>
          {value && (
            <Badge variant="outline" className="text-xs">
              {value.split(' ').length} words
            </Badge>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-slate-500" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-500" />
        )}
      </button>
      
      {isExpanded && (
        <div className="px-3 pb-3">
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="min-h-[120px] resize-none bg-white/50 border-0 focus-visible:ring-1 focus-visible:ring-brand-teal"
          />
        </div>
      )}
    </div>
  );
};

const TranscriptSOAPEditor: React.FC<TranscriptSOAPEditorProps> = ({
  transcriptLines,
  transcriptId,
  patientId,
  patientName,
  appointmentId,
  encounterId,
  clinicId,
  onSave,
  onClose,
  isOpen = true,
}) => {
  const { toast } = useToast();
  
  // SOAP note state
  const [subjective, setSubjective] = useState('');
  const [objective, setObjective] = useState('');
  const [assessment, setAssessment] = useState('');
  const [plan, setPlan] = useState('');
  
  // UI state
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'transcript' | 'soap'>('transcript');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  // Format transcript for display
  const formattedTranscript = transcriptLines
    .filter(line => line.isFinal)
    .map(line => ({
      ...line,
      time: new Date(line.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }));

  // Generate SOAP note from transcript using AI
  const generateSOAPNote = useCallback(async () => {
    setIsGenerating(true);
    setGenerationError(null);

    try {
      let response: GeneratedSOAP | null = null;

      // Priority 1: Use transcriptId if available (saved transcript)
      if (transcriptId) {
        console.log('[TranscriptSOAPEditor] Generating SOAP from saved transcript:', transcriptId);
        const result = await transcriptService.generateSOAPFromTranscript(clinicId, transcriptId);
        console.log('[TranscriptSOAPEditor] SOAP generation result:', result);
        if (result.success && result.note) {
          response = result.note;
        } else {
          throw new Error('SOAP generation returned invalid response: ' + JSON.stringify(result));
        }
      }
      // Priority 2: Use encounterId if available
      else if (encounterId) {
        console.log('[TranscriptSOAPEditor] Generating SOAP from encounter:', encounterId);
        const result = await transcriptService.generateSOAPFromEncounter(clinicId, encounterId);
        console.log('[TranscriptSOAPEditor] SOAP generation result:', result);
        if (result.success && result.note) {
          response = result.note;
        } else {
          throw new Error('SOAP generation returned invalid response: ' + JSON.stringify(result));
        }
      }
      // Priority 3: Save transcript first, then generate (for live calls)
      else if (transcriptLines.length > 0) {
        console.log('[TranscriptSOAPEditor] Saving transcript first, then generating SOAP');
        
        // Save transcript from live call
        const transcriptText = transcriptLines
          .filter(line => line.isFinal)
          .map(line => `${line.speakerName}: ${line.text}`)
          .join('\n');

        if (transcriptText.trim().length < 50) {
          toast({
            title: 'Transcript Too Short',
            description: 'Please wait for more conversation before generating a SOAP note.',
            variant: 'destructive',
          });
          setIsGenerating(false);
          return;
        }

        const saveResponse = await transcriptService.saveTranscript(clinicId, {
          patient_id: patientId,
          appointment_id: appointmentId,
          encounter_id: encounterId,
          transcript_lines: transcriptLines.filter(l => l.isFinal).map(l => ({
            speakerName: l.speakerName,
            speakerId: l.speakerId,
            text: l.text,
            timestamp: l.timestamp,
            isFinal: l.isFinal,
          })),
          duration_seconds: undefined, // Could calculate from timestamps
        });

        if (saveResponse.success && saveResponse.transcript.id) {
          console.log('[TranscriptSOAPEditor] Transcript saved, generating SOAP from:', saveResponse.transcript.id);
          
          // Small delay to ensure transcript is fully persisted
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Now generate SOAP from the saved transcript
          const result = await transcriptService.generateSOAPFromTranscript(
            clinicId,
            saveResponse.transcript.id
          );
          
          console.log('[TranscriptSOAPEditor] SOAP generation result:', result);
          
          if (result.success && result.note) {
            response = result.note;
          } else {
            throw new Error('SOAP generation returned invalid response');
          }
        } else {
          throw new Error('Failed to save transcript: ' + JSON.stringify(saveResponse));
        }
      } else {
        toast({
          title: 'No Transcript Available',
          description: 'There is no transcript content to generate a SOAP note from.',
          variant: 'destructive',
        });
        setIsGenerating(false);
        return;
      }

      if (response) {
        setSubjective(response.subjective || '');
        setObjective(response.objective || '');
        setAssessment(response.assessment || '');
        setPlan(response.plan || '');
        setHasGenerated(true);
        setActiveTab('soap');

        toast({
          title: 'SOAP Note Generated',
          description: 'AI-generated SOAP note is ready for your review and edits.',
        });
      }
    } catch (error: any) {
      console.error('[TranscriptSOAPEditor] Failed to generate SOAP:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Unable to generate SOAP note. Please try again or enter manually.';
      setGenerationError(errorMessage);
      toast({
        title: 'Generation Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  }, [transcriptLines, transcriptId, encounterId, clinicId, patientId, appointmentId, toast]);

  // Save SOAP note to patient record using EMR clinical note API
  const handleSave = useCallback(async () => {
    if (!subjective && !objective && !assessment && !plan) {
      toast({
        title: 'Empty Note',
        description: 'Please add content to at least one section before saving.',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);

    try {
      // Use EMR clinical note API
      const noteData: any = {
        patient_id: patientId,
        note_type: 'soap',
        note_date: new Date().toISOString(),
        subjective: subjective || null,
        objective: objective || null,
        assessment: assessment || null,
        plan: plan || null,
        content: transcriptId 
          ? `[AI-Generated from Telehealth Transcript ${transcriptId}]`
          : '[AI-Generated from Telehealth Call]',
        is_signed: false,
        is_addendum: false,
        is_locked: false,
      };

      // Add optional fields only if they exist
      if (encounterId) noteData.encounter_id = encounterId;
      if (appointmentId) noteData.appointment_id = appointmentId;

      console.log('[TranscriptSOAPEditor] Saving note with data:', noteData);
      
      const note = await emrService.createNote(clinicId, noteData);
      
      console.log('[TranscriptSOAPEditor] Note saved successfully:', note);

      toast({
        title: 'SOAP Note Saved',
        description: 'The clinical note has been saved to the patient record.',
      });
      onSave?.(note);
      setShowConfirmDialog(false);
    } catch (error: any) {
      console.error('[TranscriptSOAPEditor] Failed to save SOAP:', error);
      toast({
        title: 'Save Failed',
        description: error.response?.data?.error || error.message || 'Unable to save SOAP note. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  }, [clinicId, patientId, encounterId, appointmentId, transcriptId, subjective, objective, assessment, plan, onSave, toast]);

  // Check if there are unsaved changes
  const hasContent = subjective || objective || assessment || plan;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 gap-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b bg-gradient-to-r from-brand-teal/5 to-brand-sky-blue/5">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <FileText className="h-5 w-5 text-brand-teal" />
                Clinical Documentation
              </DialogTitle>
              <DialogDescription className="mt-1">
                {patientName ? `Patient: ${patientName}` : 'Generate and edit SOAP notes from the call transcript'}
              </DialogDescription>
            </div>
            <Badge variant="outline" className="bg-brand-teal/10 text-brand-teal border-brand-teal/30">
              <Clock className="h-3 w-3 mr-1" />
              {formattedTranscript.length} transcript lines
            </Badge>
          </div>
        </DialogHeader>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'transcript' | 'soap')} className="h-full flex flex-col">
            <TabsList className="mx-6 mt-4 grid w-auto grid-cols-2 bg-slate-100">
              <TabsTrigger value="transcript" className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Transcript
              </TabsTrigger>
              <TabsTrigger value="soap" className="flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4" />
                SOAP Note
                {hasGenerated && <CheckCircle className="h-3 w-3 text-green-500" />}
              </TabsTrigger>
            </TabsList>

            {/* Transcript Tab */}
            <TabsContent value="transcript" className="flex-1 p-6 pt-4 overflow-hidden">
              <div className="h-full flex flex-col">
                {/* Generation prompt */}
                {!hasGenerated && transcriptLines.length > 0 && (
                  <Alert className="mb-4 bg-brand-sky-blue/10 border-brand-sky-blue/30">
                    <Sparkles className="h-4 w-4 text-brand-sky-blue" />
                    <AlertTitle>AI Documentation Ready</AlertTitle>
                    <AlertDescription className="flex items-center justify-between">
                      <span>Generate a SOAP note from this transcript using AI</span>
                      <Button
                        onClick={generateSOAPNote}
                        disabled={isGenerating}
                        size="sm"
                        className="bg-brand-sky-blue hover:bg-brand-sky-blue/90"
                      >
                        {isGenerating ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="mr-2 h-4 w-4" />
                            Generate SOAP
                          </>
                        )}
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}

                {generationError && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Generation Error</AlertTitle>
                    <AlertDescription>{generationError}</AlertDescription>
                  </Alert>
                )}

                {/* Transcript content */}
                <Card className="flex-1 overflow-hidden">
                  <CardHeader className="py-3 px-4 border-b">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-brand-soft-teal" />
                      Call Transcript
                    </CardTitle>
                  </CardHeader>
                  <ScrollArea className="h-[400px]">
                    <CardContent className="p-4 space-y-3">
                      {formattedTranscript.length === 0 ? (
                        <div className="text-center text-slate-500 py-8">
                          <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
                          <p>No transcript available</p>
                          <p className="text-sm mt-1">Transcript will appear here during the call</p>
                        </div>
                      ) : (
                        formattedTranscript.map((line, index) => {
                          const isProvider = line.speakerName.toLowerCase().includes('provider') ||
                                            line.speakerName.toLowerCase().includes('doctor');
                          const showSpeaker = index === 0 || 
                            formattedTranscript[index - 1].speaker !== line.speaker;

                          return (
                            <div key={line.id}>
                              {showSpeaker && (
                                <div className="flex items-center gap-2 mt-3 mb-1">
                                  <div className={`p-1 rounded-full ${
                                    isProvider ? 'bg-brand-teal/20' : 'bg-brand-gold/20'
                                  }`}>
                                    {isProvider ? (
                                      <Stethoscope className={`h-3 w-3 ${
                                        isProvider ? 'text-brand-teal' : 'text-brand-gold'
                                      }`} />
                                    ) : (
                                      <User className="h-3 w-3 text-brand-gold" />
                                    )}
                                  </div>
                                  <span className={`text-xs font-medium ${
                                    isProvider ? 'text-brand-teal' : 'text-brand-gold'
                                  }`}>
                                    {line.speakerName}
                                  </span>
                                  <span className="text-[10px] text-slate-400">
                                    {line.time}
                                  </span>
                                </div>
                              )}
                              <p className="text-sm text-slate-700 pl-6">
                                {line.text}
                              </p>
                            </div>
                          );
                        })
                      )}
                    </CardContent>
                  </ScrollArea>
                </Card>
              </div>
            </TabsContent>

            {/* SOAP Note Tab */}
            <TabsContent value="soap" className="flex-1 p-6 pt-4 overflow-hidden">
              <ScrollArea className="h-[500px]">
                <div className="space-y-4 pr-4">
                  {/* Subjective */}
                  <SOAPSection
                    title="Subjective"
                    icon={User}
                    value={subjective}
                    onChange={setSubjective}
                    placeholder="Chief complaint, history of present illness, patient-reported symptoms, relevant medical history..."
                    color="border-blue-400/30"
                  />

                  {/* Objective */}
                  <SOAPSection
                    title="Objective"
                    icon={Activity}
                    value={objective}
                    onChange={setObjective}
                    placeholder="Physical examination findings, vital signs, telehealth observations..."
                    color="border-green-400/30"
                  />

                  {/* Assessment */}
                  <SOAPSection
                    title="Assessment"
                    icon={Brain}
                    value={assessment}
                    onChange={setAssessment}
                    placeholder="Diagnosis, clinical impressions, differential diagnoses..."
                    color="border-purple-400/30"
                  />

                  {/* Plan */}
                  <SOAPSection
                    title="Plan"
                    icon={Target}
                    value={plan}
                    onChange={setPlan}
                    placeholder="Treatment plan, medications, follow-up, referrals, patient education..."
                    color="border-orange-400/30"
                  />

                  {/* Regenerate button */}
                  {hasGenerated && (
                    <div className="pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={generateSOAPNote}
                        disabled={isGenerating}
                        className="text-brand-sky-blue border-brand-sky-blue/30 hover:bg-brand-sky-blue/10"
                      >
                        <RefreshCw className={`mr-2 h-4 w-4 ${isGenerating ? 'animate-spin' : ''}`} />
                        Regenerate from Transcript
                      </Button>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t bg-slate-50">
          <div className="flex items-center justify-between w-full">
            <div className="text-xs text-slate-500">
              {hasContent ? (
                <span className="flex items-center gap-1">
                  <Edit3 className="h-3 w-3" />
                  Unsaved changes
                </span>
              ) : (
                <span>Add content to save</span>
              )}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={() => setShowConfirmDialog(true)}
                disabled={!hasContent || isSaving}
                className="bg-brand-teal hover:bg-brand-teal/90"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save to Patient Record
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>

        {/* Confirm Save Dialog */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm Save</DialogTitle>
              <DialogDescription>
                This SOAP note will be permanently saved to {patientName || 'the patient'}'s medical record.
                Please ensure all information is accurate.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
                Review Again
              </Button>
              <Button 
                onClick={handleSave}
                disabled={isSaving}
                className="bg-brand-teal hover:bg-brand-teal/90"
              >
                {isSaving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="mr-2 h-4 w-4" />
                )}
                Confirm & Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default TranscriptSOAPEditor;

